<script lang="ts">
  import { onMount } from 'svelte';
  import { login, logout, session, bootstrapAuth } from '../lib/auth.svelte';
  import { isKeycloakMode } from '../lib/keycloak';
  import {
    getTenant,
    isPlatformAdminRole,
    listAllTenants,
    localProduct,
    patchTenant,
    provisionTenant,
    PRODUCTO_APP,
    PRODUCTO_LABEL,
    type Producto,
    type TenantSummary,
  } from '../lib/platform-admin';
  import { PLANES_CLINICA, PLANES_HOSPITAL, TIPOS_CLINICA } from '../lib/platform-plans';

  type Vista = 'inicio' | 'clientes' | 'alta' | 'detalle';
  type FiltroProducto = 'todos' | Producto;

  let vista = $state<Vista>('inicio');
  let loading = $state(true);
  let error = $state<string | null>(null);
  let okMsg = $state<string | null>(null);
  let tenants = $state<TenantSummary[]>([]);
  let apiErrors = $state<Partial<Record<Producto, string>>>({});
  let filtro = $state('');
  let filtroProducto = $state<FiltroProducto>('todos');
  let soloClientes = $state(true);
  let seleccionado = $state<TenantSummary | null>(null);
  let detalle = $state<Record<string, unknown> | null>(null);

  let productoAlta = $state<Producto>('clinica');
  let nombre = $state('');
  let email = $state('');
  let telefono = $state('');
  let whatsapp = $state('');
  let cargo = $state('');
  let slugSugerido = $state('');
  let planClinica = $state('clinica');
  let planHospital = $state('hospital-starter');
  let tipoOrganizacion = $state('clinica-medica');
  let diasTrial = $state(30);
  let resultadoAlta = $state<{ usuarioAdmin: string; tempPassword?: string; tenantId: string; producto: Producto } | null>(null);

  let editEstado = $state('trial');
  let editPlan = $state('');
  let editNombre = $state('');
  let editTrial = $state('');

  const clientes = $derived(tenants.filter(t => !t.esDemo));
  const demos = $derived(tenants.filter(t => t.esDemo));

  function statsFor(prod: Producto) {
    const c = clientes.filter(t => t.producto === prod);
    return {
      clientes: c.length,
      trial: c.filter(t => t.estado === 'trial').length,
      activos: c.filter(t => t.estado === 'active').length,
    };
  }

  const stats = $derived({
    total: tenants.length,
    clientes: clientes.length,
    trial: clientes.filter(t => t.estado === 'trial').length,
    activos: clientes.filter(t => t.estado === 'active').length,
    suspendidos: clientes.filter(t => t.estado === 'suspended' || t.estado === 'cancelled').length,
    clinica: statsFor('clinica'),
    hospital: statsFor('hospital'),
  });

  const filtrados = $derived(
    tenants.filter(t => {
      if (filtroProducto !== 'todos' && t.producto !== filtroProducto) return false;
      if (soloClientes && t.esDemo) return false;
      const q = filtro.trim().toLowerCase();
      if (!q) return true;
      return t.tenantId.toLowerCase().includes(q)
        || (t.nombre ?? '').toLowerCase().includes(q)
        || (t.contactoEmail ?? '').toLowerCase().includes(q);
    }),
  );

  function ir(v: Vista) {
    vista = v;
    error = null;
    okMsg = null;
    if (v !== 'detalle') seleccionado = null;
  }

  async function cargarLista() {
    loading = true;
    error = null;
    try {
      const r = await listAllTenants();
      tenants = r.tenants;
      apiErrors = r.errors;
      okMsg = 'Datos actualizados';
      setTimeout(() => { okMsg = null; }, 2500);
    } catch (e: unknown) {
      error = e instanceof Error ? e.message : 'Error cargando clientes';
    } finally {
      loading = false;
    }
  }

  async function abrirDetalle(t: TenantSummary) {
    seleccionado = t;
    vista = 'detalle';
    error = null;
    try {
      const d = await getTenant(t.producto, t.tenantId);
      detalle = d as unknown as Record<string, unknown>;
      editEstado = String(d.estado ?? 'trial');
      editPlan = String(d.plan ?? '');
      editNombre = String(d.nombre ?? '');
      editTrial = d.trialEndsAt ? d.trialEndsAt.slice(0, 16) : '';
    } catch (e: unknown) {
      error = e instanceof Error ? e.message : 'Error cargando detalle';
    }
  }

  async function guardarDetalle() {
    if (!seleccionado) return;
    error = null;
    loading = true;
    try {
      await patchTenant(seleccionado.producto, seleccionado.tenantId, {
        nombre: editNombre.trim() || undefined,
        plan: editPlan.trim() || undefined,
        estado: editEstado,
        trialEndsAt: editTrial ? `${editTrial}:00-06:00` : undefined,
      });
      await cargarLista();
      okMsg = 'Cliente actualizado';
      ir('clientes');
    } catch (e: unknown) {
      error = e instanceof Error ? e.message : 'No se pudo guardar';
    } finally {
      loading = false;
    }
  }

  async function submitAlta(e: SubmitEvent) {
    e.preventDefault();
    error = null;
    loading = true;
    resultadoAlta = null;
    const plan = productoAlta === 'clinica' ? planClinica : planHospital;
    const tipoOrg = productoAlta === 'clinica' ? tipoOrganizacion : 'hospital-privado';
    try {
      const r = await provisionTenant(productoAlta, {
        nombre: nombre.trim(),
        email: email.trim(),
        telefono: telefono.trim(),
        whatsapp: whatsapp.trim() || telefono.trim(),
        plan,
        tipoOrganizacion: tipoOrg,
        slugSugerido: slugSugerido.trim() || undefined,
        cargo: cargo.trim() || undefined,
        diasTrial,
      });
      resultadoAlta = {
        tenantId: r.tenant.tenantId,
        usuarioAdmin: r.usuarioAdmin,
        tempPassword: r.tempPassword,
        producto: productoAlta,
      };
      await cargarLista();
      okMsg = 'Cliente creado correctamente';
    } catch (e: unknown) {
      error = e instanceof Error ? e.message : 'Error en alta';
    } finally {
      loading = false;
    }
  }

  let loginUser = $state('admin@toscanasystems.com');
  let loginPass = $state('');
  let loginErr = $state<string | null>(null);
  let loginBusy = $state(false);

  async function submitLogin(e: SubmitEvent) {
    e.preventDefault();
    loginErr = null;
    loginBusy = true;
    loading = true;
    try {
      await login(undefined, loginUser.trim(), loginPass);
      if (!session.isAuth) throw new Error('Credenciales incorrectas');
      if (isPlatformAdminRole(session.value?.roles)) await cargarLista();
    } catch (e: unknown) {
      loginErr = e instanceof Error ? e.message : 'No se pudo iniciar sesión';
      loading = false;
    } finally {
      loginBusy = false;
    }
  }

  onMount(() => {
    document.documentElement.setAttribute('data-theme', 'light');
    productoAlta = localProduct();
    try { sessionStorage.setItem('toscana.skipAutoDemo', '1'); } catch { /* ignore */ }
    void bootstrapAuth().finally(async () => {
      if (session.isAuth && isPlatformAdminRole(session.value?.roles)) {
        await cargarLista();
      } else {
        loading = false;
      }
    });
  });

  const esAdmin = $derived(isPlatformAdminRole(session.value?.roles));
  const apiOk = $derived(Object.keys(apiErrors).length === 0 && !loading);
</script>

<div class="platform-page">
  {#if !session.isAuth}
    <div class="login-shell">
      <section class="login-card">
        <div class="login-hero">
          <img src="https://www.toscanasystems.com/assets/img/logo.png" alt="Toscana Systems" />
          <h1>Panel de administración</h1>
          <p>Toscana Systems · Clínicas + Hospitales</p>
        </div>
        <div class="login-body">
          <p>Gestione clientes, trials, planes y altas manuales.</p>
          {#if isKeycloakMode()}
            <form class="login-form" onsubmit={submitLogin}>
              <label>Usuario
                <input type="email" autocomplete="username" bind:value={loginUser} required />
              </label>
              <label>Contraseña
                <input type="password" autocomplete="current-password" bind:value={loginPass} required />
              </label>
              {#if loginErr}<p class="login-err">{loginErr}</p>{/if}
              <button type="submit" class="btn btn-primary" disabled={loginBusy}>
                {loginBusy ? 'Entrando…' : 'Entrar al panel'}
              </button>
            </form>
          {:else}
            <p class="muted">Configure <code>VITE_AUTH_MODE=keycloak</code> para acceso de plataforma.</p>
          {/if}
          <a class="link-out" href="https://www.toscanasystems.com/pages/sistema-medico/clinica-privada.php" target="_blank" rel="noopener">← Volver al sitio comercial</a>
        </div>
      </section>
    </div>
  {:else if !esAdmin}
    <div class="login-shell">
      <section class="login-card">
        <div class="login-hero warn">
          <h1>Sin permiso</h1>
          <p>Su cuenta no tiene rol <code>admin-platform</code></p>
        </div>
        <div class="login-body">
          <p>Use <strong>admin@toscanasystems.com</strong> o solicite acceso al equipo Toscana.</p>
          <button type="button" class="btn btn-secondary" onclick={() => logout()}>Cerrar sesión</button>
        </div>
      </section>
    </div>
  {:else}
    <div class="shell">
      <aside class="sidebar">
        <div class="sidebar-brand">
          <img src="https://www.toscanasystems.com/assets/img/logo.png" alt="Toscana" />
          <div>
            <strong>Toscana Admin</strong>
            <small>Clínicas + Hospitales</small>
          </div>
        </div>
        <nav class="sidebar-nav">
          <button type="button" class:active={vista === 'inicio'} onclick={() => ir('inicio')}>
            <span class="ico">📊</span> Inicio
          </button>
          <button type="button" class:active={vista === 'clientes' || vista === 'detalle'} onclick={() => ir('clientes')}>
            <span class="ico">👥</span> Clientes
          </button>
          <button type="button" class:active={vista === 'alta'} onclick={() => { ir('alta'); resultadoAlta = null; }}>
            <span class="ico">➕</span> Alta manual
          </button>
        </nav>
        <div class="sidebar-foot">
          <div class="user-block">
            <span class="user-label">Sesión</span>
            <span class="user-email">{session.value?.usuario}</span>
          </div>
          <button type="button" class="btn btn-logout" onclick={() => logout()}>Cerrar sesión</button>
          <a href="https://clinica-privada.com/" target="_blank" rel="noopener" class="link-mini">Ver app pública</a>
        </div>
      </aside>

      <div class="main">
        <header class="page-header">
          <div>
            <h1>
              {#if vista === 'inicio'}Panel de administración
              {:else if vista === 'clientes'}Clientes
              {:else if vista === 'alta'}Alta manual
              {:else if seleccionado}Detalle · {seleccionado.nombre}
              {/if}
            </h1>
            <p>Gestione tenants de clínicas y hospitales · APIs plataforma-ms</p>
          </div>
          <div class="header-actions">
            <button type="button" class="btn btn-secondary" onclick={() => cargarLista()} disabled={loading}>↻ Actualizar</button>
          </div>
        </header>

        {#if okMsg}
          <div class="status-box success">{okMsg}</div>
        {/if}
        {#if error}
          <div class="status-box error">{error}</div>
        {/if}

        {#if vista === 'inicio'}
          <div class="card">
            <h2>📊 Estado del sistema</h2>
            {#if apiOk}
              <div class="status-box success">✅ Conexión con clínicas y hospitales · {stats.total} tenants en catálogo</div>
            {:else if loading}
              <div class="status-box info">⏳ Sincronizando datos…</div>
            {:else}
              <div class="status-box error">❌ Una o más APIs no respondieron</div>
            {/if}
            {#each (['clinica', 'hospital'] as const) as prod}
              {#if apiErrors[prod]}
                <div class="status-box error">⚠️ {PRODUCTO_LABEL[prod]}: {apiErrors[prod]}</div>
              {/if}
            {/each}
            <div class="config-grid">
              <div class="config-item"><span class="kpi-label">Total clientes</span><span class="kpi-value">{stats.clientes}</span></div>
              <div class="config-item"><span class="kpi-label">Clínicas</span><span class="kpi-value">{stats.clinica.clientes}</span></div>
              <div class="config-item"><span class="kpi-label">Hospitales</span><span class="kpi-value">{stats.hospital.clientes}</span></div>
              <div class="config-item"><span class="kpi-label">En trial</span><span class="kpi-value">{stats.trial}</span></div>
              <div class="config-item"><span class="kpi-label">Activos</span><span class="kpi-value">{stats.activos}</span></div>
              <div class="config-item"><span class="kpi-label">Demos</span><span class="kpi-value">{demos.length}</span></div>
            </div>
            <div class="actions">
              <button type="button" class="btn btn-primary" onclick={() => ir('clientes')}>👥 Ver clientes</button>
              <button type="button" class="btn btn-accent" onclick={() => { ir('alta'); resultadoAlta = null; }}>➕ Alta manual</button>
              <a class="btn btn-secondary" href="https://www.toscanasystems.com/pages/sistema-medico/clinica-privada.php" target="_blank" rel="noopener">🌐 Sitio comercial</a>
            </div>
          </div>

          <div class="card">
            <h2>📚 Operación diaria</h2>
            <div class="info-box">
              <h3>Operación diaria</h3>
              <ul>
                <li><strong>Este panel</strong> — altas, trials, planes, estado de pago y contacto comercial.</li>
                <li>Catálogo unificado: <strong>clinica-privada.com</strong> + <strong>app.toscana-hospital.com</strong>.</li>
                <li>Los demos se pueden ocultar en la lista de clientes.</li>
              </ul>
            </div>
          </div>

          {#if clientes.length > 0}
            <div class="card">
              <h2>🕐 Últimos clientes</h2>
              <div class="table-wrap inner">
                <table>
                  <thead>
                    <tr><th>Producto</th><th>Cliente</th><th>Plan</th><th>Estado</th><th></th></tr>
                  </thead>
                  <tbody>
                    {#each clientes.slice(0, 5) as t (t.tenantId)}
                      <tr>
                        <td><span class="badge badge-{t.producto}">{t.producto === 'clinica' ? 'Clínica' : 'Hospital'}</span></td>
                        <td><strong>{t.nombre}</strong><small>{t.tenantId}</small></td>
                        <td>{t.plan ?? '—'}</td>
                        <td><span class="badge estado-{t.estado}">{t.estado}</span></td>
                        <td><button type="button" class="link" onclick={() => abrirDetalle(t)}>Ver →</button></td>
                      </tr>
                    {/each}
                  </tbody>
                </table>
              </div>
            </div>
          {/if}

        {:else if vista === 'clientes'}
          <div class="card compact-top">
            <div class="toolbar">
              <input type="search" placeholder="Buscar nombre, tenant, email…" bind:value={filtro} />
              <select bind:value={filtroProducto} class="select-inline">
                <option value="todos">Todos los productos</option>
                <option value="clinica">Solo clínicas</option>
                <option value="hospital">Solo hospitales</option>
              </select>
              <label class="check"><input type="checkbox" bind:checked={soloClientes} /> Ocultar demos</label>
            </div>
          </div>

          {#if loading && tenants.length === 0}
            <div class="status-box info">Cargando clientes…</div>
          {:else}
            <div class="card table-card">
              <h2>👥 Catálogo de clientes <span class="count">{filtrados.length} / {tenants.length}</span></h2>
              <div class="table-wrap inner">
                <table>
                  <thead>
                    <tr>
                      <th>Producto</th>
                      <th>Cliente</th>
                      <th>Plan</th>
                      <th>Estado</th>
                      <th>Trial hasta</th>
                      <th>Contacto</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {#each filtrados as t (t.tenantId)}
                      <tr class:demo={t.esDemo}>
                        <td><span class="badge badge-{t.producto}">{t.producto === 'clinica' ? 'Clínica' : 'Hospital'}</span></td>
                        <td>
                          <strong>{t.nombre}</strong>
                          <small>{t.tenantId}</small>
                          {#if t.esDemo}<span class="badge badge-demo">Demo</span>{/if}
                        </td>
                        <td>{t.plan ?? '—'}</td>
                        <td><span class="badge estado-{t.estado}">{t.estado}</span></td>
                        <td>{t.trialEndsAt ? t.trialEndsAt.slice(0, 10) : '—'}</td>
                        <td>
                          {#if t.contactoEmail}<small>{t.contactoEmail}</small>{/if}
                          {#if t.contactoTelefono}<small>{t.contactoTelefono}</small>{/if}
                        </td>
                        <td><button type="button" class="link" onclick={() => abrirDetalle(t)}>Ver →</button></td>
                      </tr>
                    {:else}
                      <tr><td colspan="7" class="empty">Sin resultados para la búsqueda actual.</td></tr>
                    {/each}
                  </tbody>
                </table>
              </div>
            </div>
          {/if}

        {:else if vista === 'alta'}
          <div class="card">
            <h2>➕ Alta manual de cliente</h2>
            <p class="sub">Crea tenant + usuario <code>@toscanasystems.com</code> + trial configurable.</p>
            {#if resultadoAlta}
              <div class="status-box success">
                <strong>Cliente creado: {resultadoAlta.tenantId}</strong>
                <p>Usuario: <code>{resultadoAlta.usuarioAdmin}</code></p>
                {#if resultadoAlta.tempPassword}
                  <p>Contraseña temporal: <code>{resultadoAlta.tempPassword}</code></p>
                {/if}
              </div>
            {/if}
            <form class="form-grid" onsubmit={submitAlta}>
              <label class="full">Producto *
                <select bind:value={productoAlta}>
                  <option value="clinica">Clínicas privadas</option>
                  <option value="hospital">Hospital privado</option>
                </select>
              </label>
              <label class="full">Nombre comercial *<input bind:value={nombre} required /></label>
              <label>Email titular *<input type="email" bind:value={email} required /></label>
              <label>Teléfono *<input bind:value={telefono} required /></label>
              <label>WhatsApp<input bind:value={whatsapp} /></label>
              <label>Cargo<input bind:value={cargo} /></label>
              {#if productoAlta === 'clinica'}
                <label>Plan *
                  <select bind:value={planClinica}>
                    {#each PLANES_CLINICA as p}<option value={p.slug}>{p.nombre} — Q{p.precio}</option>{/each}
                  </select>
                </label>
                <label>Tipo org *
                  <select bind:value={tipoOrganizacion}>
                    {#each TIPOS_CLINICA as t}<option value={t.value}>{t.label}</option>{/each}
                  </select>
                </label>
              {:else}
                <label>Plan *
                  <select bind:value={planHospital}>
                    {#each PLANES_HOSPITAL as p}<option value={p.slug}>{p.nombre} — Q{p.precio}</option>{/each}
                  </select>
                </label>
                <label>Tipo org<input value="Hospital privado" readonly disabled /></label>
              {/if}
              <label>Slug sugerido<input bind:value={slugSugerido} placeholder="auto desde nombre" /></label>
              <label>Días de trial<input type="number" min="1" max="365" bind:value={diasTrial} /></label>
              <div class="full actions">
                <button type="submit" class="btn btn-primary" disabled={loading}>{loading ? 'Creando…' : 'Crear cliente'}</button>
                <button type="button" class="btn btn-secondary" onclick={() => ir('clientes')}>Cancelar</button>
              </div>
            </form>
          </div>

        {:else if vista === 'detalle' && seleccionado}
          <div class="card">
          <button type="button" class="back" onclick={() => ir('clientes')}>← Volver a clientes</button>
          <h2>{seleccionado.nombre}</h2>
          <p class="sub">
            <span class="badge badge-{seleccionado.producto}">{PRODUCTO_LABEL[seleccionado.producto]}</span>
            Tenant <code>{seleccionado.tenantId}</code> · Login: <code>{seleccionado.usuarioLogin}</code>
            · App: <a href={PRODUCTO_APP[seleccionado.producto]} target="_blank" rel="noopener">{PRODUCTO_APP[seleccionado.producto]}</a>
          </p>
            <form class="form-grid" onsubmit={(e) => { e.preventDefault(); void guardarDetalle(); }}>
              <label class="full">Nombre<input bind:value={editNombre} /></label>
              <label>Plan<input bind:value={editPlan} placeholder="clinica-solo, clinica, clinica-plus" /></label>
              <label>Estado
                <select bind:value={editEstado}>
                  <option value="trial">trial</option>
                  <option value="active">active</option>
                  <option value="suspended">suspended</option>
                  <option value="cancelled">cancelled</option>
                </select>
              </label>
              <label class="full">Fin trial (local)<input type="datetime-local" bind:value={editTrial} /></label>
              <div class="full actions">
                <button type="submit" class="btn btn-primary" disabled={loading}>Guardar cambios</button>
              </div>
            </form>
            {#if detalle?.contacto}
              <div class="meta">
                <h3>Contacto registrado</h3>
                <pre>{JSON.stringify(detalle.contacto, null, 2)}</pre>
              </div>
            {/if}
          </div>
        {/if}
      </div>
    </div>
  {/if}
</div>

<style>
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Poppins:wght@600;700&display=swap');

  .platform-page {
    --ts-teal: var(--brand-strong);
    --ts-teal-dark: var(--brand-deep);
    --ts-black: var(--text-primary);
    --ts-muted: var(--text-secondary);
    --ts-line: var(--border);
    --ts-soft: var(--brand-soft);
    --sidebar-w: 260px;
    min-height: 100vh;
    font-family: Inter, system-ui, sans-serif;
    background: var(--bg-base);
    color: var(--text-primary);
  }

  :global(body:has(.platform-page)) { background: var(--bg-base) !important; margin: 0; }

  /* Login */
  .login-shell {
    min-height: 100vh;
    display: grid;
    place-items: center;
    padding: 1.5rem;
  }
  .login-card {
    width: min(440px, 100%);
    background: #fff;
    border-radius: 10px;
    overflow: hidden;
    box-shadow: 0 4px 24px rgba(0,0,0,.12);
  }
  .login-hero {
    background: var(--ts-black);
    color: #fff;
    padding: 2rem 1.75rem;
    text-align: center;
  }
  .login-hero.warn { background: #7f1d1d; }
  .login-hero img { height: 48px; margin-bottom: .75rem; filter: brightness(0) invert(1); }
  .login-hero h1 { font-family: Poppins, Inter, sans-serif; font-size: 1.35rem; margin: 0 0 .35rem; }
  .login-hero p { margin: 0; opacity: .85; font-size: .9rem; }
  .login-body { padding: 1.75rem; }
  .login-body p { margin: 0 0 .75rem; line-height: 1.55; color: #374151; }
  .login-form label { display: block; margin-bottom: .85rem; font-size: .78rem; font-weight: 700; color: #374151; text-transform: uppercase; letter-spacing: .02em; }
  .login-form input { display: block; width: 100%; box-sizing: border-box; margin-top: .35rem; padding: .65rem .75rem; border: 1px solid #ddd; border-radius: 6px; font: inherit; font-weight: 400; text-transform: none; }
  .login-err { color: #b91c1c; background: #fef2f2; padding: .55rem .75rem; border-radius: 6px; font-size: .85rem; }
  .link-out { display: inline-block; margin-top: 1rem; font-size: .85rem; color: var(--ts-teal-dark); }

  /* Shell */
  .shell { display: flex; min-height: 100vh; }
  .sidebar {
    width: var(--sidebar-w);
    flex-shrink: 0;
    background: var(--ts-black);
    color: #fff;
    display: flex;
    flex-direction: column;
    padding: 1.25rem 1rem;
  }
  .sidebar-brand {
    display: flex;
    align-items: center;
    gap: .65rem;
    padding: .5rem .35rem 1.25rem;
    border-bottom: 1px solid rgba(255,255,255,.12);
    margin-bottom: 1rem;
  }
  .sidebar-brand img { height: 36px; filter: brightness(0) invert(1); }
  .sidebar-brand strong { display: block; font-family: Poppins, Inter, sans-serif; font-size: .95rem; }
  .sidebar-brand small { color: rgba(255,255,255,.55); font-size: .72rem; }
  .sidebar-nav { display: flex; flex-direction: column; gap: .35rem; flex: 1; }
  .sidebar-nav button {
    display: flex; align-items: center; gap: .55rem;
    width: 100%; text-align: left;
    padding: .65rem .75rem;
    border: 0; border-radius: 8px;
    background: transparent; color: rgba(255,255,255,.85);
    font: inherit; font-weight: 600; font-size: .88rem;
    cursor: pointer;
  }
  .sidebar-nav button:hover { background: rgba(255,255,255,.08); }
  .sidebar-nav button.active { background: var(--brand-deep); color: var(--brand-on); }
  .ico { font-size: 1rem; }
  .sidebar-foot {
    border-top: 1px solid rgba(255,255,255,.12);
    padding-top: 1rem;
    display: flex; flex-direction: column; gap: .5rem;
  }
  .user-block { padding: 0 .35rem; }
  .user-label { display: block; font-size: .68rem; text-transform: uppercase; letter-spacing: .04em; color: rgba(255,255,255,.45); }
  .user-email { font-size: .78rem; word-break: break-all; color: rgba(255,255,255,.8); }
  .link-mini { font-size: .75rem; color: rgba(255,255,255,.5); text-decoration: none; padding: 0 .35rem; }
  .link-mini:hover { color: #fff; }

  .main { flex: 1; min-width: 0; padding: 1.25rem 1.5rem 2rem; }
  .page-header {
    background: var(--ts-black);
    color: #fff;
    padding: 1.5rem 1.75rem;
    border-radius: 8px;
    margin-bottom: 1.25rem;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 1rem;
    flex-wrap: wrap;
  }
  .page-header h1 {
    font-family: Poppins, Inter, sans-serif;
    font-size: 1.35rem;
    margin: 0 0 .35rem;
  }
  .page-header p { margin: 0; opacity: .75; font-size: .88rem; }

  /* Kulub-style blocks */
  .card {
    background: #fff;
    padding: 1.5rem 1.75rem;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0,0,0,.08);
    margin-bottom: 1.25rem;
  }
  .card.compact-top { padding: 1rem 1.25rem; }
  .card h2 {
    font-size: 1.05rem;
    margin: 0 0 1rem;
    color: #333;
    display: flex;
    align-items: center;
    gap: .5rem;
    flex-wrap: wrap;
  }
  .count { font-size: .78rem; font-weight: 600; color: var(--ts-muted); margin-left: auto; }
  .sub { color: var(--ts-muted); font-size: .88rem; margin: 0 0 1rem; }

  .status-box {
    padding: .85rem 1rem;
    border-radius: 6px;
    margin-bottom: 1rem;
    font-size: .9rem;
  }
  .status-box.success { background: #d4edda; border: 1px solid #c3e6cb; color: #155724; }
  .status-box.error { background: #f8d7da; border: 1px solid #f5c6cb; color: #721c24; }
  .status-box.info { background: #d1ecf1; border: 1px solid #bee5eb; color: #0c5460; }
  .status-box p { margin: .35rem 0 0; }

  .config-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: .85rem;
    margin-bottom: 1.25rem;
  }
  .config-item {
    background: #f8f9fa;
    padding: .85rem 1rem;
    border-radius: 6px;
  }
  .kpi-label {
    font-size: .68rem;
    color: #666;
    text-transform: uppercase;
    font-weight: 700;
    letter-spacing: .03em;
    display: block;
  }
  .kpi-value {
    display: block;
    font-size: 1.35rem;
    color: #111;
    font-weight: 700;
    margin-top: .25rem;
  }
  .kpi-sm { font-size: .95rem; word-break: break-all; }

  .info-box {
    background: #fff3cd;
    border: 1px solid #ffc107;
    color: #856404;
    padding: 1rem 1.15rem;
    border-radius: 6px;
  }
  .info-box h3 { font-size: .95rem; margin: 0 0 .65rem; }
  .info-box ul { margin: 0; padding-left: 1.25rem; }
  .info-box li { margin-bottom: .35rem; font-size: .88rem; }

  .actions { display: flex; gap: .65rem; flex-wrap: wrap; }

  .btn {
    padding: .65rem 1.1rem;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    font-size: .88rem;
    font-weight: 700;
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: background .2s;
  }
  .btn:disabled { opacity: .55; cursor: not-allowed; }
  .btn-primary { background: var(--ts-black); color: #fff; }
  .btn-primary:hover:not(:disabled) { background: #333; }
  .btn-accent { background: linear-gradient(135deg, #10b981, #059669); color: #fff; }
  .btn-secondary { background: #666; color: #fff; }
  .btn-secondary:hover:not(:disabled) { background: #555; }
  .btn-logout { background: #ef4444; color: #fff; width: 100%; font-size: .82rem; padding: .55rem; }
  .btn-logout:hover { background: #dc2626; }

  .toolbar { display: flex; gap: .75rem; flex-wrap: wrap; align-items: center; }
  .toolbar input[type=search] {
    flex: 1; min-width: 200px;
    padding: .6rem .75rem;
    border: 1px solid #ddd;
    border-radius: 6px;
    font: inherit;
  }
  .check { font-size: .85rem; color: var(--ts-muted); display: flex; align-items: center; gap: .35rem; white-space: nowrap; }

  .table-wrap.inner { overflow-x: auto; margin-top: .5rem; }
  table { width: 100%; border-collapse: collapse; }
  th, td { padding: .75rem .85rem; text-align: left; border-bottom: 1px solid #ddd; vertical-align: top; }
  th { background: #f8f9fa; font-weight: 700; font-size: .75rem; text-transform: uppercase; letter-spacing: .03em; color: #555; }
  td strong { display: block; color: #111; font-size: .9rem; }
  td small { display: block; color: var(--ts-muted); font-size: .78rem; }
  tr.demo { opacity: .7; }
  .empty { text-align: center; color: var(--ts-muted); padding: 2rem !important; }

  .badge {
    display: inline-block;
    padding: .2rem .5rem;
    font-size: .68rem;
    font-weight: 700;
    border-radius: 4px;
    text-transform: uppercase;
  }
  .badge-demo { background: #e5e7eb; color: #374151; margin-left: .35rem; }
  .badge-clinica { background: #ecfdf5; color: #0f766e; }
  .badge-hospital { background: #dbeafe; color: #1e40af; }
  .select-inline { padding: .55rem .65rem; border: 1px solid #ddd; border-radius: 6px; font: inherit; }
  .estado-trial { background: #fef3c7; color: #92400e; }
  .estado-active { background: var(--ts-soft); color: var(--ts-teal-dark); }
  .estado-suspended { background: #fee2e2; color: #991b1b; }
  .estado-cancelled { background: #f3f4f6; color: #6b7280; }

  .link { background: none; border: 0; color: var(--ts-teal-dark); font-weight: 700; cursor: pointer; font-size: .85rem; }
  .back { background: none; border: 0; color: var(--ts-teal-dark); font-weight: 600; cursor: pointer; margin-bottom: .75rem; padding: 0; }

  .form-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: .85rem 1rem;
  }
  .form-grid .full { grid-column: 1 / -1; }
  label { display: block; font-size: .78rem; font-weight: 700; color: #374151; text-transform: uppercase; letter-spacing: .02em; }
  input, select {
    display: block; width: 100%; box-sizing: border-box;
    margin-top: .35rem; padding: .6rem .75rem;
    border: 1px solid #ddd; border-radius: 6px;
    font: inherit; font-weight: 400; text-transform: none;
  }

  .meta { margin-top: 1.25rem; border-top: 1px solid var(--ts-line); padding-top: 1rem; }
  .meta h3 { font-size: .9rem; margin: 0 0 .5rem; }
  .meta pre { background: #f8f9fa; padding: .75rem; border-radius: 6px; font-size: .78rem; overflow: auto; margin: 0; }
  code { background: #f0f0f0; padding: .1rem .35rem; border-radius: 3px; font-size: .85em; }
  .muted { color: var(--ts-muted); }

  @media (max-width: 900px) {
    .shell { flex-direction: column; }
    .sidebar { width: 100%; flex-direction: row; flex-wrap: wrap; align-items: center; gap: .5rem; }
    .sidebar-nav { flex-direction: row; flex-wrap: wrap; flex: unset; }
    .sidebar-foot { width: 100%; flex-direction: row; flex-wrap: wrap; align-items: center; }
    .btn-logout { width: auto; }
    .form-grid { grid-template-columns: 1fr; }
  }
</style>
